<template>
  <div :class="['chart-wrapper', { 'chart-wrapper--plain': !showBackground }]">
    <template v-if="loading">
      <h3 class="chart-title">{{ title }}</h3>
      <div
        class="line-chart__skeleton"
        :style="{ height: `${height - 40}px` }"
      >
        <div
          v-for="n in 4"
          :key="n"
          class="line-chart__skeleton-bar"
        />
      </div>
    </template>
    <template v-else-if="!hasData">
      <h3
        v-if="title"
        class="chart-title"
      >
        {{ title }}
      </h3>
      <bi-chart-empty :style="{ minHeight: `${height}px` }" />
    </template>
    <template v-else>
      <h3 class="chart-title">
        {{ title }}
        <InfoTooltip
          v-if="infoTooltip"
          :label="infoTooltip.label"
          :text="infoTooltip.text"
        />
      </h3>
      <v-chart
        ref="chart"
        class="chart"
        :style="{ height: `${height}px` }"
        :option="option"
        autoresize
        @click="onPointClick"
      />
    </template>
  </div>
</template>

<script>
import VChart from 'vue-echarts'
import BiChartEmpty from './BiChartEmpty.vue'
import InfoTooltip from '../../common/InfoTooltip.vue'
import { hasChartData } from './chartData'

export default {
  name: 'LineChart',
  components: { VChart, BiChartEmpty, InfoTooltip },
  props: {
    title: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    categories: { type: Array, default: () => [] },
    values: { type: Array, default: () => [] },
    seriesName: { type: String, default: 'Valor' },
    color: { type: String, default: '#769DEB' },
    areaColor: { type: String, default: '' },
    areaGradient: {
      type: Array,
      default: () => ['rgba(118,157,235,1)', 'rgba(141,208,218,1)'],
    },
    unit: { type: String, default: '' },
    decimals: { type: Number, default: 2 },
    tooltipSubtitle: { type: String, default: '' },
    valueFormatter: { type: Function, default: null },
    target: { type: Number, default: null },
    targetLabel: { type: String, default: '' },
    showTarget: { type: Boolean, default: true },
    yAxisFormatter: { type: Function, default: null },
    tooltipFormatter: { type: Function, default: null },
    infoTooltip: { type: Object, default: null },
    height: { type: Number, default: 309 },
    showBackground: { type: Boolean, default: true },
    boldCategoryLabels: { type: Boolean, default: false },
  },
  computed: {
    hasData() {
      return hasChartData(this.values)
    },
    hasTarget() {
      return this.showTarget && this.target !== null
    },
    configuredSeries() {
      const series = [
        {
          name: this.seriesName,
          type: 'line',
          data: this.values,
          smooth: false,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: { width: 2, color: this.color },
          itemStyle: { color: this.color },
          areaStyle: this.areaColor
            ? { color: this.areaColor }
            : {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: this.areaGradient[0] },
                    { offset: 1, color: this.areaGradient[1] },
                  ],
                },
              },
        },
      ]

      if (this.hasTarget) {
        series.push({
          name: this.targetLabel,
          type: 'line',
          data: Array(this.categories.length).fill(this.target),
          symbol: 'none',
          lineStyle: { color: 'rgba(20, 85, 40, 1)', width: 2, type: 'solid' },
          itemStyle: { color: 'rgba(20, 85, 40, 1)' },
        })
      }

      return series
    },
    formatValue() {
      return val => {
        if (val === null || val === undefined) return '-'
        if (this.valueFormatter) return this.valueFormatter(val)
        return this.decimals > 0 ? val.toFixed(this.decimals).replace('.', ',') : `${val}`
      }
    },
    option() {
      const defaultYAxisFormatter = val => `${this.formatValue(val)}${this.unit}`
      const defaultTooltipFormatter = params => {
        const p = params[0]
        const label = p.axisValueLabel || p.axisValue.replace('\n', '/')
        const subtitle = this.tooltipSubtitle ? `<br/>${this.tooltipSubtitle}` : ''
        const targetLine = this.hasTarget
          ? `<br/>${this.targetLabel}: &nbsp;<b>${this.formatValue(this.target)}${this.unit}</b>`
          : ''
        return `<b style="font-size:13px">${label}</b>${subtitle}<br/>
              ${this.seriesName}: &nbsp;<b>${this.formatValue(p.value)}${this.unit}</b>${targetLine}`
      }

      return {
        color: [this.color],
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            lineStyle: { color: '#555', width: 1, type: 'solid' },
            label: { show: false },
          },
          backgroundColor: '#fff',
          borderColor: '#ddd',
          borderWidth: 1,
          padding: [10, 14],
          textStyle: { color: '#333', fontSize: 12 },
          formatter: this.tooltipFormatter || defaultTooltipFormatter,
        },
        legend: {
          bottom: 0,
          left: 0,
          show: this.hasTarget,
          data: [{ name: this.targetLabel, icon: 'path://M0,5 L16,5 L16,6.5 L0,6.5 Z' }],
          textStyle: { fontSize: 14, color: '#555' },
        },
        grid: {
          top: 20,
          left: 10,
          right: 20,
          bottom: this.hasTarget ? 50 : 12,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: this.categories,
          axisLabel: {
            fontSize: 12,
            fontWeight: this.boldCategoryLabels ? 'bold' : 'normal',
            interval: 0,
            lineHeight: 14,
            color: '#5a5a5a',
            opacity: 1,
          },
          axisLine: { lineStyle: { color: '#ddd' } },
          axisTick: { show: false },
        },
        yAxis: {
          type: 'value',
          min: this.hasTarget ? 0 : null,
          axisLabel: {
            formatter: this.yAxisFormatter || defaultYAxisFormatter,
            fontSize: 12,
            color: '#5a5a5a',
          },
          splitLine: { lineStyle: { type: 'solid', color: '#f0f0f0' } },
        },
        series: this.configuredSeries,
      }
    },
  },
  methods: {
    onPointClick(params) {
      this.$emit('point-click', { category: params.name, value: params.value })
    },
  },
}
</script>

<style scoped lang="scss">
.chart-wrapper {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  flex: 1;
  box-sizing: border-box;
  font-family: 'Roboto', sans-serif;

  &--plain {
    background: none;
    padding: 0;
    box-shadow: none;
  }
}

.chart-title {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 700;
  color: rgba(70, 70, 70, 1);
  margin: 0 0 12px;
}

.chart {
  width: 100%;
}

@keyframes bi-shimmer {
  0% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.9;
  }
  100% {
    opacity: 0.4;
  }
}

.line-chart__skeleton {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 16px 0 8px;
  box-sizing: border-box;
}

.line-chart__skeleton-bar {
  flex: 1;
  border-radius: 2px;
  background: #e9e9e9;
  animation: bi-shimmer 1.4s ease-in-out infinite;

  &:nth-child(1) {
    height: 60%;
  }
  &:nth-child(2) {
    height: 85%;
  }
  &:nth-child(3) {
    height: 45%;
  }
  &:nth-child(4) {
    height: 75%;
  }
}
</style>
